/* eslint-disable max-lines */

/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MoreMenuButton } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { CaretDownIcon } from '@modernloop/shared/icons';
import { ButtonBase, Collapse, MenuItem, Stack, Tooltip } from '@mui/material';

import { ActorType, ResponseStatus, SourceType } from 'src/generated/mloop-graphql';

import DurationLabel from 'src/components/DurationLabel';
import Link from 'src/components/Link';
import { FCWithFragments } from 'src/components/types';

import { ActivityLogBaseLike, ActivityLogTaskDetailsLike, MenuOption } from 'src/entities/ActivityLog/types';
import TaskName from 'src/entities/common/TaskName';

// eslint-disable-next-line modernloop/restrict-imports.cjs
import { SidePanelManager } from 'src/views-new/sidepanel/common/SidePanelManager';

import ActivityLogAvatar from '../ActivityLogAvatar';
import ActivityLogText from '../ActivityLogText';
import { useAiAgentLabel } from '../useAiAgentLabel';

interface Fragments {
  activityLog: ActivityLogBaseLike;
}

export interface Props {
  children?: React.ReactNode;
  dataTestId?: string;
  actionLabel?: string | JSX.Element;
  actionLabelKey?: string;
  actionLabelValues?: Record<string, unknown>;
  actionLabelTooltip?: string;
  responseStatus?: ResponseStatus | null | undefined;
  moreMenuOptions?: MenuOption[];
  onMoreMenuOptionSelect?: (option: MenuOption) => void;

  avatar?: JSX.Element;
  moreMenuDataTestId?: string;
  actorName?: string;
  /** Hide the actor name text — useful when plaintext already includes the actor */
  hideActorName?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const ActivityLogBase: FCWithFragments<Fragments, Props> = ({
  dataTestId,
  activityLog,
  actionLabel,
  actionLabelKey,
  actionLabelValues,
  actionLabelTooltip,
  responseStatus,
  moreMenuOptions,
  onMoreMenuOptionSelect,
  moreMenuDataTestId,
  children,
  avatar,
  actorName: actorNameProp,
  hideActorName = false,
  collapsible = false,
  defaultExpanded = true,
  isExpanded,
  onExpandedChange,
}) => {
  const { t, i18n } = useTranslation();
  const { activityActorType, actorEmployee, source, sourceType, createdAt: timestamp } = activityLog;
  const localTimezone = getLocalTimezone();
  const aiAgentLabel = useAiAgentLabel();
  const [expanded, setExpanded] = useState(defaultExpanded);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (isExpanded !== undefined) {
      setExpanded(isExpanded);
    }
  }, [isExpanded]);

  const resolvedActionLabel = useMemo(() => {
    if (actionLabelKey) {
      return t(actionLabelKey, {
        ...actionLabelValues,
        defaultValue: typeof actionLabel === 'string' ? actionLabel : '',
      });
    }

    return actionLabel;
  }, [actionLabel, actionLabelKey, actionLabelValues, t]);

  const actorName = useMemo(() => {
    if (actorNameProp) {
      return actorNameProp;
    }
    switch (activityActorType) {
      case ActorType.Employee:
        return actorEmployee?.fullName ?? '';
      case ActorType.System:
        return 'ModernLoop';
      case ActorType.Candidate:
        return t('hiring-dashboard-common:common.hiringProcess.candidateColumn', { defaultValue: 'Candidate' });
      case ActorType.Agent:
        return aiAgentLabel;
      default:
        return '';
    }
  }, [activityActorType, actorEmployee?.fullName, actorNameProp, aiAgentLabel, t]);

  const localizedTimestampLabel = useMemo(() => {
    return new Intl.DateTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: localTimezone,
    }).format(new Date(timestamp));
  }, [i18n.language, i18n.resolvedLanguage, localTimezone, timestamp]);

  const onBehalfOfEmployee = useMemo(() => {
    if (!actorName) {
      return null;
    }
    switch (activityActorType) {
      case ActorType.System:
        if (activityLog?.details?.__typename === 'ActivityLogTaskDetails') {
          const taskDetails = activityLog.details as ActivityLogTaskDetailsLike;
          if (taskDetails.task?.createdBy) {
            return taskDetails.task.createdBy;
          }
        }
        return null;
      default:
        return null;
    }
  }, [activityActorType, activityLog, actorName]);

  const resolvedExpanded = isExpanded ?? expanded;

  const toggleExpanded = () => {
    if (!collapsible) {
      return;
    }

    const nextExpanded = !resolvedExpanded;
    onExpandedChange?.(nextExpanded);
    if (isExpanded === undefined) {
      setExpanded(nextExpanded);
    }
  };

  return (
    <Stack direction="row" spacing={1} sx={{ width: '100%', overflow: 'hidden' }}>
      {avatar ?? <ActivityLogAvatar actorName={actorName} activityLog={activityLog} responseStatus={responseStatus} />}

      <Stack direction="column" spacing={0.5} sx={{ paddingTop: '1px', minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              minWidth: 0,
              flex: 1,
              cursor: collapsible ? 'pointer' : 'default',
            }}
            onClick={toggleExpanded}
            onKeyDown={(event) => {
              if (!collapsible) {
                return;
              }
              if (event.key !== 'Enter' && event.key !== ' ') {
                return;
              }
              event.preventDefault();
              toggleExpanded();
            }}
            role={collapsible ? 'button' : undefined}
            tabIndex={collapsible ? 0 : undefined}
            aria-expanded={collapsible ? resolvedExpanded : undefined}
          >
            <ActivityLogText data-testid={dataTestId}>
              {!hideActorName && (
                <ActivityLogText fontWeight={600} color="foreground" noWrap>
                  {actorName}&nbsp;
                </ActivityLogText>
              )}
              {onBehalfOfEmployee?.fullName && (
                <>
                  (
                  <ActivityLogText color="foreground" noWrap>
                    {t('hiring-dashboard-common:common.activityLog.onBehalfOf')}&nbsp;
                  </ActivityLogText>
                  <ActivityLogText fontWeight={600} color="foreground" noWrap>
                    {onBehalfOfEmployee?.fullName}
                  </ActivityLogText>
                  )&nbsp;
                </>
              )}
              <ActivityLogText tooltip={actionLabelTooltip}>{resolvedActionLabel}</ActivityLogText>
              <ActivityLogText>&nbsp;·&nbsp;</ActivityLogText>

              <Tooltip title={localizedTimestampLabel}>
                <span>
                  <DurationLabel
                    timestamp={timestamp}
                    typographyProps={{
                      variant: 'caption',
                      fontWeight: 400,
                      color: 'text.secondary',
                      noWrap: true,
                      component: 'span',
                    }}
                  />
                </span>
              </Tooltip>

              {/**
               * source?.count !== undefined is a workaround for checking if the fragment was returned by BE.
               * We can look for a better check in future.
               */}

              {sourceType === SourceType.SourceTypeTask && source && source.__typename === 'Task' && source.name ? (
                <ActivityLogText>
                  &nbsp;
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    component="button"
                    style={{
                      textDecorationThickness: '1.5px',
                    }}
                    href=""
                    onClick={(event) => {
                      event.stopPropagation();
                      SidePanelManager.openScheduleTaskSidePanel(source.id);
                    }}
                  >
                    <ActivityLogText color="info">
                      (<TaskName task={source} />)
                    </ActivityLogText>
                  </Link>
                </ActivityLogText>
              ) : null}
            </ActivityLogText>
          </Stack>
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0, ml: 'auto', minWidth: 28 }}>
            {moreMenuOptions ? (
              <Stack
                sx={{ my: -0.5 }}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <MoreMenuButton dataTestId={moreMenuDataTestId} size="small">
                  {moreMenuOptions.map((option) => {
                    return (
                      <MenuItem key={option.id?.toString() ?? ''} onClick={() => onMoreMenuOptionSelect?.(option)}>
                        {option.value}
                      </MenuItem>
                    );
                  })}
                </MoreMenuButton>
              </Stack>
            ) : null}
            {collapsible ? (
              <ButtonBase
                aria-label={
                  resolvedExpanded
                    ? t('hiring-dashboard-candidates:candidates.activityLog.collapseActivityLog', {
                        defaultValue: 'Collapse activity log',
                      })
                    : t('hiring-dashboard-candidates:candidates.activityLog.expandActivityLog', {
                        defaultValue: 'Expand activity log',
                      })
                }
                onClick={(event) => {
                  event.stopPropagation();
                  toggleExpanded();
                }}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '4px',
                  color: 'text.disabled',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CaretDownIcon
                  sx={{
                    fontSize: 16,
                    transform: resolvedExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 150ms ease',
                  }}
                />
              </ButtonBase>
            ) : null}
          </Stack>
        </Stack>

        {collapsible ? (
          <Collapse in={resolvedExpanded} unmountOnExit>
            {children}
          </Collapse>
        ) : (
          children
        )}
      </Stack>
    </Stack>
  );
};

ActivityLogBase.fragments = {
  activityLog: gql`
    ${ActivityLogAvatar.fragments.activityLog}
    ${TaskName.fragments.task}
    fragment ActivityLogBase_activityLog on ActivityLog {
      id
      source {
        ... on Task {
          id
          __typename
        }
        ... on Task @include(if: $showTaskName) {
          ...TaskName_task
        }
      }
      createdAt
      sourceType
      activityActorType
      actorEmployee {
        id
        fullName
        internalProfilePictureUrl
      }
      details {
        ... on ActivityLogTaskDetails {
          __typename
          task {
            id
            createdBy {
              id
              fullName
            }
          }
        }
      }
      ...ActivityLogAvatar_activityLog
    }
  `,
};

export default ActivityLogBase;
